import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Search, Clock, Users, Award, ChevronRight } from 'lucide-react';
import { usePagination } from '../hooks/usePagination';
import PaginationControls from '../components/PaginationControls';
import ArtsCollegeSlideshowBackground from '../components/ArtsCollegeSlideshowBackground';

interface College {
  name: string;
  location: string;
  duration: string;
  established: string;
  cutoff: string;
  university: string;
  type: string;
  notes?: string;
}

const ArtsAndSciencePage: React.FC = () => {
  const [selectedCategory, setSelectedCategory] = useState('Deemed University');
  const [searchTerm, setSearchTerm] = useState('');

  const categories = ['Deemed University', 'Government', 'Private'];

  const colleges: Record<string, College[]> = {
    'Deemed University': [
      {
        name: "Amrita Vishwa Vidyapeetham",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "2003",
        cutoff: "85+ percentile",
        university: "Amrita Vishwa Vidyapeetham",
        type: "Deemed University"
      },
      {
        name: "Avinashilingam University",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "80+ percentile",
        university: "Avinashilingam Institute for Home Science and Higher Education for Women",
        type: "Deemed University"
      },
      {
        name: "Bharath University",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "75+ percentile",
        university: "Bharath Institute of Higher Education and Research",
        type: "Deemed University"
      },
      {
        name: "Sathyabama University",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "82+ percentile",
        university: "Sathyabama Institute of Science and Technology",
        type: "Deemed University"
      },
      {
        name: "SASTRA Deemed University",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "85+ percentile",
        university: "Shanmugha Arts, Science, Technology and Research Academy",
        type: "Deemed University"
      },
      {
        name: "B. S. Abdur Rahman Crescent Institute Of Science And Technology",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "78+ percentile",
        university: "B.S. Abdur Rahman Crescent Institute of Science and Technology",
        type: "Deemed University"
      },
      {
        name: "The Gandhigram Rural Institute-Deemed to be University",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1956",
        cutoff: "70+ percentile",
        university: "Gandhigram Rural Institute",
        type: "Deemed University"
      },
      {
        name: "Dr. M.G.R. Educational And Research Institute",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "75+ percentile",
        university: "Dr. M.G.R. Educational and Research Institute",
        type: "Deemed University"
      },
      {
        name: "Vinayaka Missions Research Foundation",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1981",
        cutoff: "72+ percentile",
        university: "Vinayaka Mission's Research Foundation",
        type: "Deemed University"
      },
      {
        name: "Meenakshi Academy of Higher Education & Research (MAHER)",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "2001",
        cutoff: "80+ percentile",
        university: "Meenakshi Academy of Higher Education and Research",
        type: "Deemed University"
      },
      {
        name: "Vels Institute of Science, Technology & Advanced Studies (VISTAS)",
        location: "Pallavaram, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "77+ percentile",
        university: "Vels Institute of Science, Technology and Advanced Studies",
        type: "Deemed University"
      },
      {
        name: "National Institute of Technology Tiruchirappalli",
        location: "Tiruchirappalli, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "95+ percentile",
        university: "National Institute of Technology Tiruchirappalli",
        type: "Deemed University"
      },
      {
        name: "Hindustan Institute of Technology & Science",
        location: "Padur, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "83+ percentile",
        university: "Hindustan Institute of Technology and Science",
        type: "Deemed University"
      },
      {
        name: "Noorul Islam Centre For Higher Education",
        location: "Kumaracoil, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "74+ percentile",
        university: "Noorul Islam Centre for Higher Education",
        type: "Deemed University"
      },
      {
        name: "Vellore Institute of Technology",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "90+ percentile",
        university: "Vellore Institute of Technology",
        type: "Deemed University"
      },
      {
        name: "Periyar Maniammai Institute of Science & Technology",
        location: "Vallam, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "76+ percentile",
        university: "Periyar Maniammai Institute of Science & Technology",
        type: "Deemed University"
      },
      {
        name: "Sri Chandrasekharendra Saraswathi Viswa Mahavidyalaya (SCSVMV)",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "73+ percentile",
        university: "Sri Chandrasekharendra Saraswathi Viswa Mahavidyalaya",
        type: "Deemed University"
      },
      {
        name: "Ponnaiyah Ramajayam Institute of Science & Technology",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "75+ percentile",
        university: "Ponnaiyah Ramajayam Institute of Science & Technology",
        type: "Deemed University"
      },
      {
        name: "Kalasalingam Academy of Research and Education",
        location: "Krishnan Kovil, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "78+ percentile",
        university: "Kalasalingam Academy of Research and Education",
        type: "Deemed University"
      },
      {
        name: "Karunya Institute of Technology and Sciences (Deemed University)",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "81+ percentile",
        university: "Karunya Institute of Technology and Sciences",
        type: "Deemed University"
      },
      {
        name: "Chettinad Academy Of Research And Education",
        location: "Kelambakkam, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "85+ percentile",
        university: "Chettinad Academy of Research and Education",
        type: "Deemed University"
      },
      {
        name: "Karpagam Academy of Higher Education",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "76+ percentile",
        university: "Karpagam Academy of Higher Education",
        type: "Deemed University"
      },
      {
        name: "St.Peter's Institute of Higher Education and Research",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "2008",
        cutoff: "79+ percentile",
        university: "St. Peter's Institute of Higher Education and Research",
        type: "Deemed University"
      },
      {
        name: "The Tamil Nadu Dr. M.G.R. Medical University",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "82+ percentile",
        university: "The Tamil Nadu Dr. M.G.R. Medical University",
        type: "Deemed University"
      }
    ],
    Government: [
      {
        name: "University of Madras",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1857",
        cutoff: "85+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "Anna University",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1978",
        cutoff: "88+ percentile",
        university: "Anna University",
        type: "Government"
      },
      {
        name: "A.A. Govt. Arts College for Men",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1965",
        cutoff: "75+ percentile",
        university: "Periyar University",
        type: "Government"
      },
      {
        name: "A.A. Govt. Arts College",
        location: "Attur, Salem, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "73+ percentile",
        university: "Periyar University",
        type: "Government"
      },
      {
        name: "Arignar Anna College (Arts and Science)",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "1978",
        cutoff: "76+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Arignar Anna Govt. Arts College for Women",
        location: "Walajapet, Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "74+ percentile",
        university: "Thiruvalluvar University",
        type: "Government"
      },
      {
        name: "Arignar Anna Govt. Arts College",
        location: "Cheyyar, Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1981",
        cutoff: "75+ percentile",
        university: "Thiruvalluvar University",
        type: "Government"
      },
      {
        name: "Arignar Anna Govt. Arts College",
        location: "Musiri, Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1983",
        cutoff: "77+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Arignar Anna Govt. Arts College",
        location: "Villupuram District, Tamil Nadu",
        duration: "3 years",
        established: "1979",
        cutoff: "72+ percentile",
        university: "Annamalai University",
        type: "Government"
      },
      {
        name: "Alagappa Govt. Arts College",
        location: "Karaikudi, Tamil Nadu",
        duration: "3 years",
        established: "1947",
        cutoff: "80+ percentile",
        university: "Alagappa University",
        type: "Government"
      },
      {
        name: "D.G. Govt. Arts College for Women",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1973",
        cutoff: "71+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Dr. Ambedkar Government Arts College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "78+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "Dr. Kalaignar Government Arts College",
        location: "Kulithalai, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "74+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Government Arts College for Women",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1976",
        cutoff: "73+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Government Arts College",
        location: "Kumbakonam, Tamil Nadu",
        duration: "3 years",
        established: "1919",
        cutoff: "79+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Government College for Women",
        location: "Kumbakonam, Tamil Nadu",
        duration: "3 years",
        established: "1960",
        cutoff: "76+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Govt. Arts and Science College for Women",
        location: "Bargur, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "70+ percentile",
        university: "Bharathiar University",
        type: "Government"
      },
      {
        name: "Govt. Arts College (Autonomous)",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1875",
        cutoff: "82+ percentile",
        university: "Bharathiar University",
        type: "Government"
      },
      {
        name: "Govt. Arts College for Men (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "80+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "Govt. Arts College for Men",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "1974",
        cutoff: "72+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Govt. Arts College for Women",
        location: "Ramanathapuram, Tamil Nadu",
        duration: "3 years",
        established: "1982",
        cutoff: "71+ percentile",
        university: "Alagappa University",
        type: "Government"
      },
      {
        name: "Govt. Arts College for Women",
        location: "Krishnagiri, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "73+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Govt. Arts College for Women",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1981",
        cutoff: "74+ percentile",
        university: "Periyar University",
        type: "Government"
      },
      {
        name: "Govt. Arts College for Women",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "75+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Ariyalur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Chidambaram, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "76+ percentile",
        university: "Annamalai University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "69+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "70+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Melur, Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "67+ percentile",
        university: "Madurai Kamaraj University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Paramakudi, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "68+ percentile",
        university: "Alagappa University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1955",
        cutoff: "77+ percentile",
        university: "Periyar University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Thiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1958",
        cutoff: "75+ percentile",
        university: "Thiruvalluvar University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1865",
        cutoff: "81+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Udagamandalam, Tamil Nadu",
        duration: "3 years",
        established: "1955",
        cutoff: "78+ percentile",
        university: "Bharathiar University",
        type: "Government"
      },
      {
        name: "Govt. Arts College",
        location: "Udumalpet, Tamil Nadu",
        duration: "3 years",
        established: "1983",
        cutoff: "72+ percentile",
        university: "Bharathiar University",
        type: "Government"
      },
      {
        name: "Govt. College for Women",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1978",
        cutoff: "73+ percentile",
        university: "Alagappa University",
        type: "Government"
      },
      {
        name: "Govt. Thirumagal Mills College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1961",
        cutoff: "76+ percentile",
        university: "Thiruvalluvar University",
        type: "Government"
      },
      {
        name: "Kundavai Nachiyar Govt. College for Women",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1947",
        cutoff: "78+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "M.R. Govt. Arts College",
        location: "Tiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "74+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "M.V. Muthiah Govt. Arts College for Women",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "72+ percentile",
        university: "Madurai Kamaraj University",
        type: "Government"
      },
      {
        name: "Muthurangam Government Arts College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "71+ percentile",
        university: "Thiruvalluvar University",
        type: "Government"
      },
      {
        name: "L.R.G. Govt. Arts College for Women",
        location: "Tiruppur, Tamil Nadu",
        duration: "3 years",
        established: "1983",
        cutoff: "73+ percentile",
        university: "Bharathiar University",
        type: "Government"
      },
      {
        name: "Loganatha Narayanasamy Govt. Arts College",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "70+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "N.K.R. Government Arts College for Women",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "71+ percentile",
        university: "Periyar University",
        type: "Government"
      },
      {
        name: "Periyar University College of Arts and Science",
        location: "Mettur, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "69+ percentile",
        university: "Periyar University",
        type: "Government"
      },
      {
        name: "Presidency College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1840",
        cutoff: "90+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "Quaid-e-Millet Government Arts College for Women (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1957",
        cutoff: "82+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "Raja Duraisingam Govt. Arts College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1981",
        cutoff: "73+ percentile",
        university: "Alagappa University",
        type: "Government"
      },
      {
        name: "Rajah Serfoji Govt.Arts College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1878",
        cutoff: "79+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "Rajeswari Vedachalam Government Arts College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "74+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "Sethupathy Govt. Arts College",
        location: "Ramanathapuram, Tamil Nadu",
        duration: "3 years",
        established: "1957",
        cutoff: "72+ percentile",
        university: "Alagappa University",
        type: "Government"
      },
      {
        name: "Sri Meenakshi Govt. College for Women (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "79+ percentile",
        university: "Madurai Kamaraj University",
        type: "Government"
      },
      {
        name: "Sri Subramaniaswami Government Arts College",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "71+ percentile",
        university: "University of Madras",
        type: "Government"
      },
      {
        name: "Thiru Govindasamy Government Arts College",
        location: "Tindivanam, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "70+ percentile",
        university: "Annamalai University",
        type: "Government"
      },
      {
        name: "Thiru Kolanjiappar Government Arts College",
        location: "Virudhachalam, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "69+ percentile",
        university: "Annamalai University",
        type: "Government"
      },
      {
        name: "Thiruvalluvar Govt. Arts College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1982",
        cutoff: "73+ percentile",
        university: "Periyar University",
        type: "Government"
      },
      {
        name: "Thiru-Vi-Ka Govt. Arts College",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "71+ percentile",
        university: "Bharathidasan University",
        type: "Government"
      },
      {
        name: "V.S. Sivalingam Govt. Arts College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "68+ percentile",
        university: "Alagappa University",
        type: "Government"
      }
    ],
    Private: [
      {
        name: "Loyola College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1925",
        cutoff: "85+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Stella Maris College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1947",
        cutoff: "82+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Asan Memorial College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "75+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "A.M. Jain College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1941",
        cutoff: "80+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "A.P.A. College of Arts and Culture",
        location: "Palani, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "70+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Auxilium College",
        location: "Katpadi, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "78+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "A.A. Arts and Science College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "72+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "A.D.M. College for Women",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "A.E.T. College",
        location: "Attur Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "65+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "A.J.K. College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "74+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "A.K.C. College of Arts and Science",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "70+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "A.K.D. Dharmaraja's Women's College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "73+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "A.N.J.A. College (Autonomous)",
        location: "Sivakasi, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "76+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "A.P.C. Mahalakshmi College for Women",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "71+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "A.R.C. Viswanathan College",
        location: "Mayiladuthurai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "A.V.C. College (Autonomous)",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1955",
        cutoff: "82+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "A.V.S. College of Arts and Science",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "A.V.V.M. Sri Pushpam College (Autonomous)",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "79+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Abi and Abi College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Adaikala Matha College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Adharsh Vidyalaya College of Arts and Science for Women",
        location: "Bhavani Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "64+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Adhiparasakthi College of Science",
        location: "Kalavai, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "70+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Aditanar College of Arts and Science",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1974",
        cutoff: "75+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Aiman College of Arts and Science for Women",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "69+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Alamelu Angappan College for Women",
        location: "Komarapalayam, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Alpha Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "72+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Ambai Arts College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "71+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Ambiga College of Arts and Science",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Ananda College",
        location: "Devakottai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "65+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Anbu Arts and Science College",
        location: "Komarapalayam, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Angappa College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "73+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Anna Adarsh College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "74+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Annai College of Arts and Science",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Annai Fathima College of Arts and Science",
        location: "Tirumangalam, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Annai Hajira Women's College",
        location: "Melapalayam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Annai Theresa College of Arts and Science",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "70+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Annai Veilankanni College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "72+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Annai Veilankanni's College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "71+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Annai Violet Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Annai Women's College",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Apollo Arts and Science College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "73+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Arakkonam Arts and Science College",
        location: "Arakkonam, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "68+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Arcot Sri Mahalakshmi Women's College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "70+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Aries Arts and Science College for Women",
        location: "Vadalur Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "65+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Arignar Anna College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "71+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Arputha College of Arts and Science",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Arul Anandar College (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1970",
        cutoff: "81+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Arulmigu Panruti Ayyan Arts and Science College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Arumugam Pillai Seethai Ammal College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "72+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Arun Krishna College of Arts and Science",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "66+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Aruna Vidhya Arts and Science College",
        location: "Chengam Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "64+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Arungarai Amman College of Arts and Science",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Attur Arts and Science College",
        location: "Attur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "65+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Auxilium College of Arts and Science for Women",
        location: "Alangudi Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Ayyan Thiruvalluvar College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1999",
        cutoff: "71+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Bhaktavatsalam Memorial College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "73+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Bharathi's Women's College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "78+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "B. Padmanabhan Jayanthimala Arts and Science College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "B.P.J College Of Arts and Science",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Best Arts and Science College",
        location: "Sirkali Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Bharath College of Science and Management",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Bharathi Arts and Science College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "70+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Bharathidasan College of Arts and Science",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "71+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Bharathiyar Arts and Science College for Women",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "69+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Bishop Ambrose College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1956",
        cutoff: "79+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Bishop Calldowell College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1954",
        cutoff: "76+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Bishop Heber College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1966",
        cutoff: "80+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Bishop Thorp College",
        location: "Dharapuram, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "74+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Bon Secours College for Women",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1971",
        cutoff: "77+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "C. Abdul Hakkim College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "68+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "C. Kandasami Naidu College for Men",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "75+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "C. Kandasamy Naidu College for Women",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "73+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "C.B.M. College Sakethapuri",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "70+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "C.P.A. College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "71+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Chellammal Women's College of the Pachaiyappa's Trust",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1942",
        cutoff: "79+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Cheraan's Arts Science College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Chikkaiah Naiacker College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1948",
        cutoff: "72+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Chikkanna Government Arts College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "74+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "C.M.S. College of Science and Commerce",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1965",
        cutoff: "76+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "C.S.I. Bishop Appasamy College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1969",
        cutoff: "75+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "C.S.I. Darling Selvabai Thavaraj David College of Arts and Science for Women",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1973",
        cutoff: "72+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "C.S.I. Edward Women's Christian College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1968",
        cutoff: "73+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "C.S.I. Jeyaraj Annapackiam College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1978",
        cutoff: "71+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "C.T.M. College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Cambridge College of Arts and Science",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Caussanel College of Arts and Science",
        location: "Ramanathapuram, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "70+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Cauveri College for Women",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "74+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Chennai National College (Arts and Science)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Cherran College For Women",
        location: "Tirupur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "66+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Chettinad College of Arts and Science",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "72+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Chevaliar T. Thomas Elizabeth College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "71+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Chidambaram Pillai College of Women",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Christhu Raj College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "70+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Dhanraj Baid Jain College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "75+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Dharmamurthi Rao Bahadur Calavala Cunnan Chetty's Hindu (D.R.B.C.C.C.H.) College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1958",
        cutoff: "77+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Dwaraka Doss Goverdhan Doss Vaishnav College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "78+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Danapackiyam Krishnasamy Mudaliar College for Women",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "71+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Devanga Arts College",
        location: "Aruppukottai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Dhanalakshmi Srinivasan College of Arts and Science for Women",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "69+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Dharmambal Ramasamy Arts and Science College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Dharmapuram Adinam Arts College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1979",
        cutoff: "70+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Don Bosco College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "72+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Dr. G.R. Damodaran College of Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1966",
        cutoff: "76+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Dr. M.G.R. Chockalingam Arts College",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Dr. M.G.R. Janaki College of Arts and Science for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Dr. N.G.P. Arts and Science College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Dr. Nallikuppusamy Arts College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "70+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Dr. R.A.N.M. Arts and Science College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Dr. R.K. Shanmugam College of Arts and Science",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Dr. R.V. Arts and Science College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "65+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Dr. S.N.S. Rajalakshmi College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Dr. Sir A.L. Mudaliar Vocational Arts and Science",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Dr. Umayal Ramanathan College for Women",
        location: "Karaikudi, Tamil Nadu",
        duration: "3 years",
        established: "1983",
        cutoff: "73+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Dr. Zakir Hussain College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "71+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "E.M.G. Yadava Women's College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1974",
        cutoff: "74+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Emerald Heights College for Women",
        location: "Udhagamandalam, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "67+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Erode Arts College (Autonomous)",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1968",
        cutoff: "79+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Ethiraj College for Women (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1948",
        cutoff: "81+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Edaiyathangudi G.S. Pillai Arts and Science College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Enathi Rajappa College of Arts and Science",
        location: "Pattukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "65+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Erode Arts College for Women",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "72+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Erode Christian College of Arts and Science for Women",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "70+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Fatima College (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1953",
        cutoff: "80+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "G.T.N. Arts College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1965",
        cutoff: "73+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Gobi Arts and Science College (Autonomous)",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "75+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Guru Nanak College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "76+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "G.V.N. College",
        location: "Kovilpatti, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "71+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Ganapathi Seethai Ammal College",
        location: "Manamadurai, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "68+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Ganesar Senthamil Kallori",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Gem Gates Arts and Science College",
        location: "Attur, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "64+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Govindammal Adithanar Women's College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1981",
        cutoff: "72+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Guru Shree Shanti Vijai Jain College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "H.H. The Rajahs College (Autonomous)",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1947",
        cutoff: "79+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "H.K.R.H. College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "71+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Harur Muthu Arts and Science College for Women",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Hindustan College of Arts and Science",
        location: "Kelambakkam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "72+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Hindusthan College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Holy Cross College (Autonomous)",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1895",
        cutoff: "82+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Holy Cross College",
        location: "Nagercoil, Tamil Nadu",
        duration: "3 years",
        established: "1957",
        cutoff: "77+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Holy Cross Home Science College for Women",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1978",
        cutoff: "74+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Idaya College of Arts and Science for Women",
        location: "Pudupalayam, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Idhaya College of Arts and Science for Women",
        location: "Kumbakonam, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Idhaya Women's College",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Imayam College of Arts and Science",
        location: "Thuraiyur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Immaculate College for Women",
        location: "Sankarapuram Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Indian Arts and Science College",
        location: "Naidumangalam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Indian Harvard Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "65+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Indira Gandhi Jayanthi Women's College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "70+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Indo-American College",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Islamiah College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1980",
        cutoff: "73+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Islamiah Women's Arts and Science College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "71+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "J.J. College of Arts and Science",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "69+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Jairam Arts and Science College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Justice Basheer Ahmed Syed College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "72+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "J.A. Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "J.K.K. Nataraja College of Arts and Science",
        location: "Komarapalayam, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "73+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Jamal Mohamed College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1951",
        cutoff: "78+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Jawahar Science College",
        location: "Neyveli, Tamil Nadu",
        duration: "3 years",
        established: "1982",
        cutoff: "74+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Jaya College of Arts and Science",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Jayagovind Harigopal Agarwal Agarsen College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "70+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Jayaraj Annapackiam College for Women (Autonomous)",
        location: "Periyakulam, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "79+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Jeyaraj Chelladurai College of Arts and Science",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "John Bosco Arts and Science College",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Joseph Arts and Science College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Jothi College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "K.S.R. College of Arts and Science",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "71+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Kamban College Arts and Science for Women",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Kandaswami Kandar's College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "75+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Khadir Mohideen College",
        location: "Adirampattinam, Tamil Nadu",
        duration: "3 years",
        established: "1957",
        cutoff: "73+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Kongunadu Arts and Science College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1986",
        cutoff: "74+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "K.C.S. Kasi Nadar College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "K.G. College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "K.M.G. College of Arts and Science",
        location: "Gudiyattam, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "K.R. Arts and Science College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "70+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "K.S. Raja College of Arts and Science for Women",
        location: "Gingee Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "K.S.G. College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "65+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Kaamadhenu Arts and Science College",
        location: "Sathyamangalam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Kairali Arts, Science and Commerce College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "66+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Kamadhenu College of Arts and Science",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Kamaraj College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "74+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Kanchi Kamatchi Amman Arts and Science College for Women",
        location: "Kanchipuram Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Kanchi Shri Krishna College of Arts and Science",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "70+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Karpagam University (Karpagam Arts and Science College)",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "72+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Karuppannan Mariappan College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "71+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Kaveripakkam Arts and Science College",
        location: "Kaveripakkam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "KAYPEEYES College of Arts and Science",
        location: "Kotagiri, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "King Nandivarman College of Arts and Science",
        location: "Tiruvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Meenakshi College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "76+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Muthayammal College of Arts and Science",
        location: "Rasipuram, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "69+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "M.S.S. Wakf Board College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "70+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madras Christian College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1837",
        cutoff: "85+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Madurai Institute of Social Sciences",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "71+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University College",
        location: "Aundipatti, Theni, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University Evening College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "65+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University Evening College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1982",
        cutoff: "67+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University Evening College",
        location: "Palani, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "66+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University Evening College",
        location: "Paramakudi, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "64+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University Evening College",
        location: "Periyakulam, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "65+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University Evening College",
        location: "Ramanathapuram, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "63+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Kamaraj University Evening College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "64+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Mannar Tirumalai Naicker College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1955",
        cutoff: "74+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Mazharul Uloom College",
        location: "Ambur, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "69+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "M.A.V.M.M. Ayira Vysiar College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "67+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "M.G.R. College",
        location: "Hosur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "M.I.E.T. College of Arts and Science",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "M.K. University College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1980",
        cutoff: "70+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "M.M.E.S. Women's Arts and Science College",
        location: "Walaja Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "65+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "M.O.P. Vaishnav College for Women (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1952",
        cutoff: "79+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Madha Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "67+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Madura College (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1889",
        cutoff: "80+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Madurai Sivakasi Nadars College for Women",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "71+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Mahalakshmi College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Maharaja Arts and Science College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "69+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Maharaja College for Women",
        location: "Erode, Perundurai, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "70+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Maharani Arts and Science Women's College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Mahatma Arts and Science College",
        location: "Illuppur Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Mahendra College of Arts and Science",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Malankara Catholic College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "73+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Mangayarkarasi Arts and Science College for Women",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Manickam College of Arts and Science",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Mar Gregorios Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Marrapan Lakshmiamma Arts and Science College for Women",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Marudhur Kesari Jain College for Women",
        location: "Vaniyampadi, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "69+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Maruthupandiyar College",
        location: "Vallam, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Mass College of Arts and Science",
        location: "Kumbakonam, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "65+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Matha College of Arts and Sciences",
        location: "Manamadurai, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "68+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Meenakshi Ammal Arts and Science College for Women",
        location: "Uthiramerur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "66+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Meenakshi Chandrasekaran College of Arts and Science",
        location: "Pattukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "65+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Meenakshi Ramasamy Arts and Science College",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "64+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Mercury College of Arts and Science",
        location: "Arakonam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Michael Job College of Arts and Science for Women",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Modern Arts and Science College",
        location: "Jayankondam, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Mohamed Sathak College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "70+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Moti and Moti Arts and Science College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Muslim Arts College",
        location: "Tiruvithamcode, Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1979",
        cutoff: "71+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Loyola College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1925",
        cutoff: "85+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Lady Doak College (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1948",
        cutoff: "81+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Lakshmi Narayanan Visalakshi College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Lakshmipuram College of Arts and Science",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "70+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Nehru Arts and Science College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "N.M.S.S. Vellachami Nadar College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "72+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Nallamuthu Gounder Mahalingam College (Autonomous)",
        location: "Pollachi, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "78+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "New College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1951",
        cutoff: "79+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Nirmala College for Women",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "75+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "N.M.S. Sermathai Vasan College for Women",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "71+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "N.P.R. Arts and Science College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Nadar Saraswathy College of Arts and Science",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "69+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Naina Mohamed College of Arts and Science",
        location: "Aranthangi Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Nandha Arts and Science College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "National College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1919",
        cutoff: "77+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Navarasam Arts and Science College for Women",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Nazareth College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Nazareth Margochis College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "70+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Nehru College of Arts and Science",
        location: "Tirupur, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Nehru Memorial College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "74+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Nesamony Memorial Christian College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1983",
        cutoff: "72+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Nethaji Subash Chandra Bose College",
        location: "Thiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "New Prince Shri Bhavani Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Nift Tea College of Knitwear Fashion",
        location: "Tirupur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Noorl Islam Arts and Science College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "69+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "P.S.G. College of Arts and Science (Autonomous)",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1951",
        cutoff: "83+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "P.S.G.R. Krishnamal College for Women",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "78+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Pachaiyappa's College for Men",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1842",
        cutoff: "76+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Pachaiyappa's College for Women",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1958",
        cutoff: "74+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Pachaiyappa's College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1842",
        cutoff: "77+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Pasumpon Muthuramalinga Thevar College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "70+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Periyar Arts College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Providence College for Women",
        location: "Udhagamandalam, Tamil Nadu",
        duration: "3 years",
        established: "1962",
        cutoff: "75+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "P.G.P. College of Arts and Science",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "P.K.N. Arts and Science College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "P.K.R. Arts College for Women",
        location: "Gobichettipalayam, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "70+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "P.M.P. College of Arts and Science",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Paavendhar College of Arts and Science",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "65+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Pachamuthu Arts and Science Women's College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Padmavani College of Arts and Science for Women",
        location: "Omalur Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "64+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Pannaikadu Veerammal Paramasivam College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Park's College",
        location: "Tirupur, Tamil Nadu",
        duration: "3 years",
        established: "1965",
        cutoff: "73+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Parvathy's Arts and Science College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Pasumbon Muthuramalinga Thevar College",
        location: "Melaneelithanallur, Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "69+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Pasumpon Thiru. Muthuramalinga Thevar College",
        location: "Ramnad, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "68+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Patrician College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "67+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Pattammal Alagesan College of Arts and Science",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Pavendar Bharadhidasan Arts and Science College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Pee Gee College of Arts and Science",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Perarignar Anna College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Periyar E.V.R. College (Autonomous)",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1949",
        cutoff: "79+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Pioneer College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Pioneer Kumarasamy College",
        location: "Nagercoil, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "70+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Ponnaiyah Ramajayam College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "72+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Poompuhar College",
        location: "Nagapattinam, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Poonga College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "65+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Pope's College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1889",
        cutoff: "75+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Prince Shri Venkateshwara Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Prof. Dhanapalan College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Quaid-e-Millet College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1954",
        cutoff: "74+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Queen Mary's College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1914",
        cutoff: "81+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "R.V.S. College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "71+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Rev. Jacob Memorial Christian College",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "73+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Rajapalayam Raju's College",
        location: "Rajapalayam, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "72+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Ramakrishna Mission Vivekananda College (Men)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1946",
        cutoff: "78+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "R.B. Gothi Jain College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "R.T.G. Arts and Science College for Women",
        location: "Gingee Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Rabiammal Ahamed Maideen College for Women",
        location: "Tiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Rabindharanath Tagore College of Arts and Science for Women",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Rajagiri Dawood Batcha College of Arts and Science",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Rajahs College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1947",
        cutoff: "72+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Rajeswari Arts and Scinece College for Women",
        location: "Vanur Taluk, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Rama Prabha College of Arts and Science",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Ramasamy Tamil College",
        location: "Karaikudi, Tamil Nadu",
        duration: "3 years",
        established: "1968",
        cutoff: "71+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Rathinam College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Rose Mary Arts and Science College",
        location: "Palayamkottai, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sourashtra College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "73+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sourashtra College for Women",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1974",
        cutoff: "71+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sri Kaliswari College",
        location: "Sivakasi, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "69+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sriram College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "S.B.K. College",
        location: "Aruppukottai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "S.D.N. Bhatt Vaishnav College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1958",
        cutoff: "76+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "S.I.V.E.T. College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "72+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sacred Heart Evening College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1980",
        cutoff: "70+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Salem Sowdeswari College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1973",
        cutoff: "71+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sarah Tucker College",
        location: "Palayamkottai, Tamil Nadu",
        duration: "3 years",
        established: "1895",
        cutoff: "78+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Saraswathy Narayanan College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1966",
        cutoff: "74+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Senthamil College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sri G.V.G. Visalakshi College for Women (Autonomous)",
        location: "Udumalpet, Tamil Nadu",
        duration: "3 years",
        established: "1968",
        cutoff: "77+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sri Ramakrishna Mission Vidyalaya College of Arts and Science (Autonomous)",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1930",
        cutoff: "85+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sri S. Ramasamy Naidu Memorial College",
        location: "Sattur, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "70+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sri Sarada College for Women (Autonomous)",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "79+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Srimath Sivagnana Balaya Swamigal Tamil College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "69+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "St. Louis College for the Deaf",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1971",
        cutoff: "65+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Stella Maris College for Women (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1947",
        cutoff: "84+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "The American College (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1881",
        cutoff: "82+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Thiagarajar College (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1949",
        cutoff: "80+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "The Standard Fireworks Rajarathinam College for Women (Autonomous)",
        location: "Sivakasi, Tamil Nadu",
        duration: "3 years",
        established: "1984",
        cutoff: "75+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "St. Josephs College (Autonomous)",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1844",
        cutoff: "83+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Women's Christian College (Autonomous)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1915",
        cutoff: "81+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "S.B.A. College of Science and Management",
        location: "Arani, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "S.K. College of Arts and Science",
        location: "Mannargudi, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "65+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "S.K.S.S. Arts College",
        location: "Thiruppanandal, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "64+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "S.M.S. College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "S.N.R. Sons College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "70+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "S.P. College of Science and Arts",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "67+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "S.R.M. Arts and Science College",
        location: "Chengalpattu, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "S.S. Duraisamy Nadar Maariammal College",
        location: "Kovilpatti, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "66+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "S.S.M. College of Arts and Science",
        location: "Komarapalayam, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "65+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "S.S.S. College Art, Science and Management",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "67+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "S.T. Hindu College",
        location: "Nagercoil, Tamil Nadu",
        duration: "3 years",
        established: "1889",
        cutoff: "75+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sadakathullah Appa College",
        location: "Palayamkottai, Tamil Nadu",
        duration: "3 years",
        established: "1940",
        cutoff: "72+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sami Arul Arts and Science College",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "64+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sankara College of Science and Commerce",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1973",
        cutoff: "71+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Saratha College of Arts and Science",
        location: "Gobichettipalayam, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sardar Raja Arts College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "68+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sattanatha Karaiyalar College",
        location: "Tenkasi, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "67+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Scott Christian College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1809",
        cutoff: "78+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "SCSVMV University (Sri Chandrasekharendra Saraswathi Viswa Mahavidyalaya)",
        location: "Kancheepuram, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "74+ percentile",
        university: "Deemed University",
        type: "Private"
      },
      {
        name: "Seethalakshmi Achi College for Women",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1948",
        cutoff: "70+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Seethalakshmi Ramaswamy College (Autonomous)",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "76+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Selvam Arts and Science College",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "68+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Semgamala Thaayar Educational Trust Women's College",
        location: "Tiruvarur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sengunthar College of Arts and Science",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Shiri Kumaran College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Shri Aravindar Arts and Science College",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Shri Chandra Prabhu Jain College for Men",
        location: "Tiruvallur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Shri Nehru Maha Vidyalaya College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "69+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Shri Raghavendra Arts and Science College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Shri Shankarlal Sundarbai Shasun Jain College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "73+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Shrimathi Indira Gandhi College (Women)",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "71+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Shunmuga Industries Arts and Science College",
        location: "Tirvuvannamalai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "66+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Siga College of Management and Computer Science",
        location: "Villupuram, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "64+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Sindhi College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1971",
        cutoff: "70+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sir Theagaraya College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "74+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sivanthi Adithanar College",
        location: "Nagercoil, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "69+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Soka Ikeda Arts and Science College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "67+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sonai Meenal Arts and Science College",
        location: "Mudukulathur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "65+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Sree Amman Arts and Science College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sree Ayyappa College for Women",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "68+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sree Devi Kumari Women's College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sree Narayana Guru College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "69+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sree Ramu College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sree Saraswathy Thyagaraja College",
        location: "Pollachi, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "70+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sree Sevugan Annamalai College",
        location: "Devakottai, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "67+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Sri Adi Chunchanagiri Women's College",
        location: "Theni, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "66+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sri Akilandeswari Women's College",
        location: "Vandavasi, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "65+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Sri Arumugam Arts and Science College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Sri Balamurugan Arts and Science College",
        location: "Mettur, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sri Bharathi Arts and Science College (W)",
        location: "Pudukkottai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sri Bharathi Women's Arts and Science College",
        location: "Arni, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Sri Bharathivelu Arts and Science College",
        location: "Walaja, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "Sri Ganesh College of Arts and Science",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "68+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sri Gee College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "69+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sri Jayendra Saraswathy Maha Vidayalaya College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "70+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sri K.G.S. Arts College",
        location: "Srivaikuntam, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "68+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sri Kandhan College of Arts and Science",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sri Kanniga Parameswari Arts and Science College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sri Krishna Arts and Science College",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1990",
        cutoff: "71+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sri Muthukumaran Arts and Science College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sri Muthukumarasamy College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "69+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sri Nagalakshmi Ammal Arts and Science College",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sri Paramakalyani College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "74+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sri Parasakthi Women's College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "70+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sri Ramakrishna College of Arts and Science for Women",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "72+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sri Ramalinga Sowdambigai College of Science and Commerce",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "69+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sri Sai Bharath College of Arts and Science",
        location: "Dindigul, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Sri Sakthi Kailash College of Women",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "68+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sri Sankara Arts and Science College",
        location: "Kanchipuram, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "70+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Sri Sankara Arts and Science College",
        location: "Kumbakonam, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "69+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sri Sarada Niketan College of Science for Women",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sri Saradha College for Women",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sri Saradha Niketan College for Women",
        location: "Sivagangai, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "68+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "Sri Saradha Women's College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Sri Thangam Periyasamy Arts and Science College",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "65+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Sri Vasavi College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "69+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sri Venkateswara College of Arts and Science",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sri Vidhya Mandir Arts and Science College",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sri Vijay Vidyalaya College of Arts & Science (Women's)",
        location: "Dharmapuri, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Sri Vinayaga College of Arts and Science",
        location: "Ulundurpet, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Sridevi Arts and Science College",
        location: "Ponneri, Tamil Nadu",
        duration: "3 years",
        established: "1998",
        cutoff: "64+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Srimad Andavan Arts and Science College",
        location: "Trichy, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "68+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Srinivasan College of Arts and Science",
        location: "Perambalur, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "St. George's Jayaraj Chelladurai College for Women",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "70+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "St. John's College",
        location: "Palayamkottai, Tamil Nadu",
        duration: "3 years",
        established: "1921",
        cutoff: "73+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "St. Joseph College of Arts and Science",
        location: "Cuddalore, Tamil Nadu",
        duration: "3 years",
        established: "1987",
        cutoff: "69+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "St. Joseph's College (Arts and Science)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "74+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "St. Joseph's College for Women",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1956",
        cutoff: "75+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "St. Joseph's College of Arts and Science for Women",
        location: "Hosur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "Bangalore University",
        type: "Private"
      },
      {
        name: "St. Jude's College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "St. Mary's College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1889",
        cutoff: "72+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "St. Thomas College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1964",
        cutoff: "71+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "St. Xavier's College",
        location: "Tirunelveli, Tamil Nadu",
        duration: "3 years",
        established: "1923",
        cutoff: "76+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Subbalakshmi Lakshmipathi College of Science",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1947",
        cutoff: "73+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Subramaniyam College of Arts and Science",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Subramanya College of Arts and Science",
        location: "Palani, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "68+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Sudharsan College of Arts and Science",
        location: "Illuppur, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Sulthana Adbullah Rowther College for Women",
        location: "Thanjavur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Syed Hammeda Arts and Science College",
        location: "Kilakkarai, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "65+ percentile",
        university: "Alagappa University",
        type: "Private"
      },
      {
        name: "V.P.M.M. Arts and Science College for Women",
        location: "Srivilliputtur, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "67+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Vels University (Vel's College of Science)",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "72+ percentile",
        university: "Vels University",
        type: "Private"
      },
      {
        name: "Vinayaka Missions Kirupananda Variyar Arts and Science College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "68+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Vysya College",
        location: "Salem, Tamil Nadu",
        duration: "3 years",
        established: "1978",
        cutoff: "70+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "V.H.N.S.N. College",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1988",
        cutoff: "66+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "V.O. Chidambaram College",
        location: "Tuticorin, Tamil Nadu",
        duration: "3 years",
        established: "1963",
        cutoff: "74+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "V.V.V. College for Women",
        location: "Virudhunagar, Tamil Nadu",
        duration: "3 years",
        established: "1985",
        cutoff: "68+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Vellalar College for Women",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1975",
        cutoff: "71+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Voorhees College",
        location: "Vellore, Tamil Nadu",
        duration: "3 years",
        established: "1898",
        cutoff: "76+ percentile",
        university: "Thiruvalluvar University",
        type: "Private"
      },
      {
        name: "V.L.B. Janakiammal College of Arts and Science",
        location: "Coimbatore, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "69+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "V.N. Krishnasamy Naidu College of Arts and Science for Women",
        location: "Mettuppalayam, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "67+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "V.T.M College of Arts and Science",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1996",
        cutoff: "66+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Vailakanni Malathy Pannicker College of Arts and Science",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1997",
        cutoff: "65+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Valliammal College for Women",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1981",
        cutoff: "72+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Valluvar College of Science and Management",
        location: "Karur, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "67+ percentile",
        university: "Bharathidasan University",
        type: "Private"
      },
      {
        name: "Vel Sri Ranga Sanku College",
        location: "Chennai, Tamil Nadu",
        duration: "3 years",
        established: "1989",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Vellakoil Arts and Science College",
        location: "Erode, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "66+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Vidhya Sagar Women's College",
        location: "Chengalpattu, Tamil Nadu",
        duration: "3 years",
        established: "1992",
        cutoff: "68+ percentile",
        university: "University of Madras",
        type: "Private"
      },
      {
        name: "Vidyasagar College of Arts and Science",
        location: "Udumalpet, Tamil Nadu",
        duration: "3 years",
        established: "1993",
        cutoff: "67+ percentile",
        university: "Bharathiar University",
        type: "Private"
      },
      {
        name: "Vivekananda Arts and Science College for Women",
        location: "Sirkali, Tamil Nadu",
        duration: "3 years",
        established: "1994",
        cutoff: "66+ percentile",
        university: "Annamalai University",
        type: "Private"
      },
      {
        name: "Vivekananda College",
        location: "Kanyakumari, Tamil Nadu",
        duration: "3 years",
        established: "1967",
        cutoff: "73+ percentile",
        university: "Manonmaniam Sundaranar University",
        type: "Private"
      },
      {
        name: "Vivekanandha College (Autonomous)",
        location: "Madurai, Tamil Nadu",
        duration: "3 years",
        established: "1972",
        cutoff: "77+ percentile",
        university: "Madurai Kamaraj University",
        type: "Private"
      },
      {
        name: "Vivekanandha College for Women",
        location: "Tiruchengode, Tamil Nadu",
        duration: "3 years",
        established: "1991",
        cutoff: "68+ percentile",
        university: "Periyar University",
        type: "Private"
      },
      {
        name: "Vivekanandha College of Arts and Science (W)",
        location: "Namakkal, Tamil Nadu",
        duration: "3 years",
        established: "1995",
        cutoff: "67+ percentile",
        university: "Periyar University",
        type: "Private"
      }
    ]
  };

  const filteredColleges = colleges[selectedCategory]?.filter(college =>
    college.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    college.location.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  // Pagination using custom hook
  const pagination = usePagination({
    data: filteredColleges,
    initialItemsPerPage: 25
  });

  // Reset pagination when category or search changes
  React.useEffect(() => {
    pagination.resetPagination();
  }, [selectedCategory, searchTerm]);

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  const TableView = () => (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">College</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cutoff</th>
             
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {pagination.currentItems.map((college, index) => (
              <motion.tr
                key={index}
                className="hover:bg-gray-50"
                variants={itemVariants}
                initial="hidden"
                animate="visible"
                transition={{ delay: index * 0.1 }}
              >
                <td className="px-6 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">{college.name}</div>
                    <div className="text-sm text-gray-500">Est. {college.established}</div>
                    {college.notes && (
                      <div className="text-xs text-orange-600 mt-1">{college.notes}</div>
                    )}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{college.location}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    college.type === 'Government' 
                      ? 'bg-green-100 text-green-800' 
                      : college.type === 'Deemed University'
                      ? 'bg-purple-100 text-purple-800'
                      : 'bg-blue-100 text-blue-800'
                  }`}>
                    {college.type}
                  </span>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{college.cutoff}</td>
                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                  
                </td>
              </motion.tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 to-blue-100">
      {/* Hero Section with Slideshow */}
      <section className="relative text-white py-16 sm:py-20 overflow-hidden" style={{ minHeight: 400 }}>
        <ArtsCollegeSlideshowBackground />
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              Arts & Science Colleges in Tamil Nadu
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-blue-100 max-w-3xl mx-auto">
              Explore top colleges offering Bachelor's programs in Arts & Science with comprehensive information about admission requirements and cutoff details.
            </p>
          </motion.div>
        </div>
      </section>

      {/* TNEA Admission Status Banner */}
      <div className="w-full bg-gradient-to-r from-red-600 via-orange-500 to-red-600 text-white py-4 overflow-hidden relative z-10 shadow-lg">
        <div className="absolute inset-0 bg-black/20"></div>
        <motion.div 
          className="whitespace-nowrap relative z-20 flex items-center"
          initial={{ x: "100vw" }}
          animate={{ x: "-100%" }}
          transition={{
            duration: 25,
            repeat: Infinity,
            ease: "linear",
            repeatType: "loop"
          }}
        >
          <div className="flex items-center space-x-8 text-xl font-bold tracking-wide">
            <span>🚨 IMPORTANT: TNEA 2025 Admissions will be closed on 30/09/2025</span>
            <span>•</span>
            <span>📞 For guidance on alternative admissions and next steps, contact our experts</span>
            <span>•</span>
            <span>🎓 We help you find the best alternative pathways</span>
            <span>•</span>
            <span>📱 Contact: +91 77086 77132 (Enquiry) | +91 93610 27132 (Advisor)</span>
            <span>•</span>
          </div>
        </motion.div>
      </div>

      {/* Search and Filter Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="bg-white rounded-xl shadow-lg p-6 mb-8"
        >
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
            {/* Search */}
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
              <input
                type="text"
                placeholder="Search colleges by name or location..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-6 py-3 rounded-lg font-medium transition-colors duration-200 ${
                    selectedCategory === category
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>
        </motion.div>

        {/* Results Summary */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mb-6"
        >
          <p className="text-gray-600">
            Showing <span className="font-semibold text-blue-600">{pagination.startIndex + 1}-{Math.min(pagination.endIndex, filteredColleges.length)}</span> of <span className="font-semibold text-blue-600">{filteredColleges.length}</span> colleges in <span className="font-semibold">{selectedCategory}</span> category
            {pagination.totalPages > 1 && (
              <span className="ml-2 text-sm text-gray-500">(Page {pagination.currentPage} of {pagination.totalPages})</span>
            )}
          </p>
        </motion.div>

        {/* College Table */}
        <TableView />

        {/* Pagination Controls */}
        {filteredColleges.length > 0 && (
          <PaginationControls
            currentPage={pagination.currentPage}
            totalPages={pagination.totalPages}
            itemsPerPage={pagination.itemsPerPage}
            totalItems={filteredColleges.length}
            startIndex={pagination.startIndex}
            endIndex={pagination.endIndex}
            onNextPage={pagination.handleNextPage}
            onPrevPage={pagination.handlePrevPage}
            onItemsPerPageChange={pagination.setItemsPerPage}
            onPageChange={pagination.goToPage}
            className="mt-8"
          />
        )}

        {filteredColleges.length === 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center py-12"
          >
            <div className="text-gray-500 text-lg">No colleges found matching your criteria.</div>
          </motion.div>
        )}
      </div>

      {/* Information Section */}
      <div className="bg-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-4">About Arts & Science Programs</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Arts & Science programs offer diverse undergraduate courses in humanities, natural sciences, computer science, 
              and various other fields that provide strong foundation for higher studies and career opportunities.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Clock,
                title: "Duration",
                description: "3 years undergraduate programs",
                color: "blue"
              },
              {
                icon: Award,
                title: "Eligibility",
                description: "12th pass in relevant stream",
                color: "green"
              },
              {
                icon: Users,
                title: "Specializations",
                description: "Multiple streams and subjects",
                color: "purple"
              },
              {
                icon: Users,
                title: "Career Scope",
                description: "Teaching, research, industry",
                color: "orange"
              }
            ].map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.1 * index }}
                className="text-center"
              >
                <div className={
                  "w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center " +
                  (item.color === "blue"
                    ? "bg-blue-100"
                    : item.color === "green"
                    ? "bg-green-100"
                    : item.color === "purple"
                    ? "bg-purple-100"
                    : "bg-orange-100")
                }>
                  <item.icon
                    className={
                      "h-8 w-8 " +
                      (item.color === "blue"
                        ? "text-blue-600"
                        : item.color === "green"
                        ? "text-green-600"
                        : item.color === "purple"
                        ? "text-purple-600"
                        : "text-orange-600")
                    }
                  />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ArtsAndSciencePage;